import { Box, Flex, Image, Input, Text, useDisclosure, useToast } from '@chakra-ui/react';
import { ChangeEvent, ChangeEventHandler, useContext, useEffect, useState } from 'react';
import { BrowserView, isMobile } from 'react-device-detect';
import { HiSearch } from 'react-icons/hi';
import { FormattedMessage } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import FilterIcon from '../assets/icons/FilterIcon.svg';
import FilterIconOff from '../assets/icons/FilterIconOff.svg';
import GridIcon from '../assets/icons/GridIcon.svg';
import ListIcon from '../assets/icons/ListIcon.svg';
import { AuthenticatedComponent } from '../auth/keycloak';
import {
    DefaultFilterDrawer,
    FILTER_ALL,
    FILTER_BINGO,
    FILTER_LEVIATHAN,
    FILTER_MARBLES,
    FILTER_RDR,
} from '../component/gameComponents/DefaultFilterDrawer';
import { LiveSixListItem } from '../component/gameComponents/LiveSixListItem';
import { SixListItemCompact } from '../component/gameComponents/SixListItemCompact';
import { SortButtons, SortOption } from '../component/gameComponents/SortButtons';
import { BrowserSidebar } from '../component/globalComponents/BrowserSidebar';
import { BrowserTopBar } from '../component/globalComponents/BrowserTopBar';
import { MobileViewPage } from '../component/globalComponents/MobileViewPage';
import { BackIcon } from '../component/iconComponents/BackIcon';
import PageDataContext from '../context/PageDataContext';
import { logSearchTerm } from '../firebase/analytics';
import { useLocalStorage } from '../hooks/useLocalStorage';
import { usePageDataCache } from '../hooks/usePageDataCache';
import { usePageSessionStorage } from '../hooks/usePageSessionStorage';
import {
    BINGO_LICENSE,
    LEVIATHAN_LICENSE,
    MARBLES_LICENSE,
    RACE_DAY_LICENSE,
} from '../service/games';
import { GameInfo, getGameName, getRoomsByUserFilter, PlatformOption } from '../service/lobby';
import { getPublicProfiles, getUserFollowing, UserProfile } from '../service/social';
import { SortingOptions } from './DiscoverPage';

type GameInfoMap = {
    [userId: string]: GameInfo;
};

export const getLiveInfo = async (
    profileList: UserProfile[],
): Promise<{ profiles: UserProfile[]; gameInfoMap: GameInfoMap }> => {
    const userIds = profileList.map((profile) => profile.id);
    const gameInfos = await getRoomsByUserFilter({
        userFilter: {
            streamsix: userIds,
        },
    });

    const gameInfoMap: GameInfoMap = {};
    gameInfos?.forEach((info) => (gameInfoMap[info.owner] = info));

    const profileRequest = profileList.map((profile) => profile.id);
    const profilesWithStream = await getPublicProfiles(profileRequest);

    return {
        profiles: profileList.map((profile) => profilesWithStream[profile.id] || profile),
        gameInfoMap: gameInfoMap,
    };
};

export const FollowingPage: AuthenticatedComponent = ({ keycloak }) => {
    const LogPrefix = 'FollowingPage';

    const { isLoadFromCache } = useContext(PageDataContext);

    const navigate = useNavigate();

    const userID = keycloak.subject;
    const [view, setView] = useLocalStorage<string>('streamsix_view', 'grid');
    const toast = useToast();
    const { isOpen, onOpen, onClose } = useDisclosure();
    const [searchText, setSearchText] = useState('');
    const FILTER_LICENSE_MAP: { [filter: string]: string } = {
        [FILTER_ALL]: '',
        [FILTER_MARBLES]: MARBLES_LICENSE,
        [FILTER_BINGO]: BINGO_LICENSE,
        [FILTER_RDR]: RACE_DAY_LICENSE,
        [FILTER_LEVIATHAN]: LEVIATHAN_LICENSE,
    };
    const [logSearchTimeout, setLogSearchTimeout] = useState<NodeJS.Timeout | undefined>(undefined);
    const [following, setFollowing] = usePageDataCache<UserProfile[] | undefined>(
        'following',
        undefined,
    );
    const [followGameInfoMap, setFollowGameInfoMap] = usePageDataCache<GameInfoMap>(
        'followingGameInfoMap',
        {},
    );

    const [filteredList, setFilteredList] = usePageDataCache<UserProfile[] | undefined>(
        'filteredList',
        undefined,
    );
    const [filteredGameInfoMap, setFilteredGameInfoMap] = usePageDataCache<GameInfoMap>(
        'filteredGameInfoMap',
        {},
    );
    const [gameTitleFilter, setGameTitleFilter] = usePageSessionStorage<string>(
        'gameTitleFilter',
        FILTER_ALL,
    );

    const [isFiltering, setIsFiltering] = usePageSessionStorage<boolean>('isFiltering', false);

    const [streamingPlatformFilter, setStreamingPlatformFilter] = usePageDataCache<
        PlatformOption | undefined
    >('streamingPlatformFilter', undefined);

    /**
     * `undefined` if none was selected
     */
    const [sortOption, setSortOption] = usePageDataCache<SortOption | undefined>(
        'discover-sorting',
        undefined,
    );

    const getFilterIcon = () => {
        if (isOpen) {
            return FilterIcon;
        } else {
            return FilterIconOff;
        }
    };

    const applyFilters = () => {
        onClose();
        setIsFiltering(true);
    };

    const fetchFollowingInformation = async () => {
        try {
            let following = await getUserFollowing(userID, keycloak.token);
            const { profiles, gameInfoMap } = await getLiveInfo(following);

            setFollowing(profiles);
            setFollowGameInfoMap(gameInfoMap);

            setFilteredGameInfoMap(gameInfoMap);
            setFilteredList(profiles);
        } catch (e) {
            toast({
                title: 'FOLLOWING - Something Went Wrong',
                description: (e as Error).message,
                status: 'error',
                duration: 9000,
                isClosable: true,
            });
        }
    };

    const resetFilters = () => {
        setGameTitleFilter(FILTER_ALL);
        setStreamingPlatformFilter(undefined);
        setSortOption(undefined);
        onClose();
        setIsFiltering(false);
    };

    const updateGameTitleFilter = (gameTitle: string) => {
        const logPrefix = `[${LogPrefix}.updateGameTitleFilter]`;
        setGameTitleFilter(gameTitle);
        console.log(`${logPrefix} set game title filter to: ${gameTitle}`);
    };

    const updateStreamingPlatformFilter = (platformString: string) => {
        const logPrefix = `[${LogPrefix}.updateStreamingPlatformFilter]`;
        const platform = platformString as PlatformOption;
        setStreamingPlatformFilter(
            Object.values(PlatformOption).includes(platform) ? platform : undefined,
        );
        console.log(`${logPrefix} set streaming platform filter to: ${platform}`);
    };

    const updateSearch: ChangeEventHandler<HTMLInputElement> = (
        event: ChangeEvent<HTMLInputElement>,
    ) => {
        const logPrefix = `[${LogPrefix}.updateSearch]`;
        const text = event.target.value.toLowerCase();
        setSearchText(text);
        console.log(`${logPrefix} set search text to: ${text}`);
    };

    const triggerSearch = () => {
        logSearch(searchText);
    };

    const logSearch = (text: string) => {
        if (text.length <= 3) {
            return;
        }

        if (!!logSearchTimeout) {
            clearTimeout(logSearchTimeout);
        }
        setLogSearchTimeout(setTimeout(() => logSearchTerm(text), 1000));
    };

    const listView = () => {
        setView('list');
    };

    const gridView = () => {
        setView('grid');
    };

    const filterLiveGame = (userProfile: UserProfile): boolean => {
        const matchesFilters = () => {
            if (!isFiltering && sortOption === undefined) {
                return true;
            }
            const gameInfo = followGameInfoMap[userProfile.id];
            if (!gameInfo) {
                return false;
            }

            return (
                (FILTER_ALL === gameTitleFilter ||
                    gameInfo.license === FILTER_LICENSE_MAP[gameTitleFilter]) &&
                (streamingPlatformFilter === undefined || !!userProfile.twitch_stream)
            );
        };

        return (
            matchesFilters() &&
            (searchText.length > 0
                ? [
                      userProfile.username,
                      userProfile.twitch_profile?.login,
                      userProfile.twitch_profile?.display_name,
                      getGameName(followGameInfoMap[userProfile.id]?.license),
                  ]
                      .map((e) => e?.toLowerCase())
                      .join(',')
                      .includes(searchText)
                : true)
        );
    };

    const cmp = (a: UserProfile, b: UserProfile): number => {
        if (!sortOption) {
            return 0;
        }
        const isDesc = sortOption.direction * 2 - 1;
        const aGameInfo: GameInfo | undefined = followGameInfoMap[a.id];
        const bGameInfo: GameInfo | undefined = followGameInfoMap[b.id];
        switch (sortOption?.attribute) {
            case SortingOptions.NumPlayers:
                return (
                    isDesc *
                    ((bGameInfo.actorInfo?.data?.activePlayerCount || 0) -
                        (aGameInfo.actorInfo?.data?.activePlayerCount || 0))
                );
            case SortingOptions.NumberViewers:
                return (
                    isDesc *
                    ((b.twitch_stream?.viewer_count || 0) - (a.twitch_stream?.viewer_count || 0))
                );
            default:
                return 0;
        }
    };

    useEffect(() => {
        if (!isLoadFromCache || following === undefined) {
            fetchFollowingInformation();
        }
    }, []);

    const renderFollowingContent = () => (
        <Box className={'following-list'}>
            {view === 'grid' ? (
                <Flex
                    className={
                        isMobile
                            ? 'current-six-flex-container mobile-flex-adjust'
                            : 'current-six-flex-container'
                    }
                >
                    {filteredList
                        ?.filter(filterLiveGame)
                        .sort(cmp)
                        .map((listItem, idx) => (
                            <LiveSixListItem
                                following={following}
                                profile={listItem}
                                index={idx}
                                gameInfo={filteredGameInfoMap[listItem.id]}
                                displayNotifications={false}
                                key={listItem.id}
                            />
                        ))}
                </Flex>
            ) : (
                <Flex className={'compact-flex-container'}>
                    {filteredList
                        ?.filter(filterLiveGame)
                        .sort(cmp)
                        .map((listItem, idx) => (
                            <SixListItemCompact
                                following={following}
                                key={listItem.id}
                                profile={listItem}
                                gameInfo={filteredGameInfoMap[listItem.id]}
                                index={idx}
                                displayNotifications={false}
                            />
                        ))}
                </Flex>
            )}
        </Box>
    );

    return (
        <>
            <BrowserView>
                <BrowserTopBar loadingColor="#3fa5f0" isLoading={following === undefined}>
                    <Flex className={'following-header flex-expand'}>
                        <Flex></Flex>
                        <Flex className={'header'}>
                            <Text>
                                <FormattedMessage
                                    id="following.title"
                                    defaultMessage={'FOLLOWING'}
                                />
                            </Text>
                        </Flex>
                        <Flex className={'header-options'}>
                            <Box className={'search-bar-browser'}>
                                <Flex className={'search-input'}>
                                    <Input
                                        variant={'unstyled'}
                                        value={searchText}
                                        onChange={updateSearch}
                                        enterKeyHint={'search'}
                                        onKeyDown={(e) => e.key === 'Enter' && triggerSearch()}
                                    />
                                    <Box className={'search-icon'} onClick={triggerSearch}>
                                        <HiSearch className={'search-svg'} />
                                    </Box>
                                </Flex>
                            </Box>
                            <Image src={getFilterIcon()} onClick={onOpen} />
                            <Image src={ListIcon} onClick={listView} />
                            <Image src={GridIcon} onClick={gridView} />
                        </Flex>
                    </Flex>
                </BrowserTopBar>
                <BrowserSidebar />
                <Box className={'following-container-browser browser-content'}>
                    <SortButtons
                        searchText={searchText}
                        sortOption={sortOption}
                        setSortOption={setSortOption}
                    />
                    <Box className={'current-sixes'}>
                        <Box className={'following-container'}>{renderFollowingContent()}</Box>
                    </Box>
                    <DefaultFilterDrawer
                        onClose={onClose}
                        isOpen={isOpen}
                        resetFilters={resetFilters}
                        applyFilters={applyFilters}
                        updateGameTitleFilter={updateGameTitleFilter}
                        gameTitleFilter={gameTitleFilter}
                        updateStreamingPlatformFilter={updateStreamingPlatformFilter}
                        streamingPlatformFilter={streamingPlatformFilter || 'ALL'}
                    />
                </Box>
            </BrowserView>
            <MobileViewPage
                title="FOLLOWING"
                titleColor="#000"
                titleBackgroundColor="#3fa5f0"
                customBanner={
                    <Flex className={'following-header'}>
                        <Flex className="discover-header-back" onClick={() => navigate(-1)}>
                            <BackIcon />
                        </Flex>
                        <Flex className={'header'}>
                            <Text>
                                <FormattedMessage
                                    id="following.title"
                                    defaultMessage={'FOLLOWING'}
                                />
                            </Text>
                        </Flex>
                        <Flex className={'header-options'}>
                            <Image src={getFilterIcon()} onClick={onOpen} />
                            <Image src={ListIcon} onClick={listView} />
                            <Image src={GridIcon} onClick={gridView} />
                        </Flex>
                    </Flex>
                }
                isLoading={following === undefined}
            >
                <Flex className={'mobile-space-above-infinite-scroll'}>
                    <SortButtons
                        searchText={searchText}
                        sortOption={sortOption}
                        setSortOption={setSortOption}
                    />
                    <Box className={'search-bar'}>
                        <Flex className={'search-input'}>
                            <Input
                                variant={'unstyled'}
                                value={searchText}
                                onChange={updateSearch}
                                enterKeyHint={'search'}
                                onKeyDown={(e) => e.key === 'Enter' && triggerSearch()}
                            />
                            <Box className={'search-icon'} onClick={triggerSearch}>
                                <HiSearch className={'search-svg'} />
                            </Box>
                        </Flex>
                    </Box>
                </Flex>
                {renderFollowingContent()}
                <DefaultFilterDrawer
                    onClose={onClose}
                    isOpen={isOpen}
                    resetFilters={resetFilters}
                    applyFilters={applyFilters}
                    updateGameTitleFilter={updateGameTitleFilter}
                    gameTitleFilter={gameTitleFilter}
                    updateStreamingPlatformFilter={updateStreamingPlatformFilter}
                    streamingPlatformFilter={streamingPlatformFilter || 'ALL'}
                />
            </MobileViewPage>
        </>
    );
};
